import React, { useEffect, useState } from 'react'
import {
  Form,
  Button,
  Row,
  Col,
  Select,
  Table,
  Space,
  DatePicker,
  Input,
  Card,
  Divider,
  Modal,
  Spin,
} from 'antd'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons'

import AdminHeader from '../CommonComponents/AdminHeader'
import AdminFooter from '../CommonComponents/AdminFooter'

import axiosInstance from '../../Utils/axiosInstance'
import {
  getChildrenList,
  saveCounsellorData,
  GetCounsellorData,
  updateCounsellorData,
  DeleteCounsellorData,
} from '../APIComponents/AdminApi'

const { Option } = Select
const { TextArea } = Input

const CounsellorForm = () => {
  //===============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'Counsellor Form')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1
  //============================================

  const [form] = Form.useForm()

  const [childrenList, setChildrenList] = useState([])
  const [list, setList] = useState([])

  const [showForm, setShowForm] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [isEdit, setIsEdit] = useState(false)
  const [recordId, setRecordId] = useState(null)

  const [loading, setLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)

  const [viewModal, setViewModal] = useState(false)
  const [viewData, setViewData] = useState(null)

  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)

  // ================= FETCH =================
  useEffect(() => {
    fetchChildren()
    fetchList()
  }, [])

  const fetchChildren = async () => {
    try {
      const res = await axiosInstance.get(getChildrenList)
      setChildrenList(res.data)
    } catch {
      toast.error('Failed to fetch children')
    }
  }

  const fetchList = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(GetCounsellorData)
      setList(res.data)
    } catch {
      toast.error('Failed to fetch data')
    } finally {
      setTableLoading(false)
    }
  }

  // ================= SUBMIT =================
  const handleFinish = async (values) => {
    try {
      setLoading(true)

      const payload = {
        reg_id: values.childName,
        date: values.date.format('YYYY-MM-DD'),
        educational_details: values.education,
        incident_analysis: values.incident,
        observations: values.observation,
        remarks: values.remarks,
        report_by: values.reportBy,
      }

      let res

      if (isEdit) {
        res = await axiosInstance.put(
          `${updateCounsellorData}/${recordId}`,
          payload,
        )

        if (res.data === 'UPDATE') {
          toast.success('Updated successfully')
          form.resetFields()
          setShowForm(false)
          setIsEdit(false)
          setRecordId(null)
          fetchList()
        } else if (res.data === 'ALREADY_EXIST') {
          toast.warn('Data already exist!')
        }
      } else {
        res = await axiosInstance.post(saveCounsellorData, payload)

        if (res.data === 'SAVED') {
          toast.success('Saved successfully')
          form.resetFields()
          setShowForm(false)
          setIsEdit(false)
          setRecordId(null)
          fetchList()
        } else if (res.data === 'ALREADY_EXIST') {
          toast.warn('Data already exist!')
        }
      }
    } catch {
      toast.error('Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  // ================= EDIT =================
  const handleEdit = (record) => {
    setShowForm(true)
    setIsEdit(true)
    setRecordId(record.reg_id)

    form.setFieldsValue({
      childName: record.reg_id,
      date: dayjs(record.date),
      education: record.educational_details,
      incident: record.incident_analysis,
      observation: record.observations,
      remarks: record.remarks,
      reportBy: record.report_by,
    })
  }

  // ================= DELETE =================
  const handleDelete = async () => {
    try {
      const res = await axiosInstance.delete(
        `${DeleteCounsellorData}/${deleteId}`,
      )

      if (res.data === 'DELETED') {
        toast.success('Deleted successfully')
        fetchList()
      }
    } catch {
      toast.error('Delete failed')
    } finally {
      setDeleteModal(false)
    }
  }

  // ================= TABLE =================
  const columns = [
    {
      title: 'Sr No',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Student Name',
      render: (_, record) =>
        `${record.student?.fname} ${record.student?.mname} ${record.student?.lname}`,
    },
    {
      title: 'Date',
      render: (record) => dayjs(record.date).format('DD-MM-YYYY'),
    },
    {
      title: 'Reported By',
      dataIndex: 'report_by',
    },
    ...(showActionColumn
      ? [
          {
            title: 'Action',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => {
                      setViewData(record)
                      setViewModal(true)
                    }}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                )}
                {pagePermission.is_delete == 1 && (
                  <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setDeleteId(record.reg_id)
                      setDeleteModal(true)
                    }}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <AdminHeader />
      <div className="student-form-section container-fluid">
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-2">Counsellor Form</h3>
          </div>
          {!showForm && (
            <>
              <Row justify="end" className="mb-3">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setShowForm(true)}
                >
                  Add
                </Button>
              </Row>

              <Table
                columns={columns}
                dataSource={list}
                rowKey="id"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  onChange: (page, size) => {
                    setCurrentPage(page)
                    setPageSize(size)
                  },
                }}
                bordered
                loading={tableLoading}
                className="custom-table shadow rounded border"
                style={{ overflowX: 'auto' }}
              />
            </>
          )}

          {/* ================= FORM ================= */}
          {showForm && (
            <Spin spinning={loading}>
              <div className="row student-form-container rounded shadow  py-2">
                <Form form={form} layout="vertical" onFinish={handleFinish}>
                  <Row
                    gutter={16}
                    className="mt-3 p-2 border rounded"
                    style={{ background: '#fafafa' }}
                  >
                    <Col xs={8} md={8}>
                      <Form.Item
                        name="childName"
                        label="Select Children Name"
                        rules={[{ required: true }]}
                      >
                        <Select
                          showSearch
                          size="large"
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option?.children
                              ?.toString()
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        >
                          {childrenList.map((child) => (
                            <Option key={child.id} value={child.id}>
                              {child.fname} {child.mname} {child.lname} (Reg No:{' '}
                              {child.reg_no})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={8} md={6}>
                      <Form.Item
                        name="date"
                        label="Date"
                        rules={[{ required: true }]}
                      >
                        <DatePicker style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>

                    <Col xs={8} md={10}>
                      <Form.Item
                        name="reportBy"
                        label="Report By"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item name="education" label="Educational Details">
                        <TextArea rows={2} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item name="incident" label="Incident Analysis">
                        <TextArea rows={2} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item name="observation" label="Observations">
                        <TextArea rows={2} />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item name="remarks" label="Remarks / Notes">
                        <TextArea rows={2} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider />

                  <Row justify="end" className="pb-3 pt-3">
                    <Space>
                      <Button
                        onClick={() => {
                          form.resetFields()
                          setShowForm(false)
                          setIsEdit(false)
                          setRecordId(null)
                        }}
                      >
                        Cancel
                      </Button>
                      <Button type="primary" htmlType="submit">
                        {isEdit ? 'Update' : 'Submit'}
                      </Button>
                    </Space>
                  </Row>
                </Form>
              </div>
            </Spin>
          )}

          {/* VIEW MODAL */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 Counsellor Details
              </div>
            }
            open={viewModal}
            onCancel={() => setViewModal(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModal(false)}
              >
                Close
              </Button>,
            ]}
            width={800}
            style={{
              top: 20,
              paddingBottom: 50,
            }}
            centered
            bodyStyle={{
              maxHeight: '75vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingBottom: 10,
            }}
          >
            {viewData && (
              <div
                style={{
                  backgroundColor: '#f5f7fa',
                  padding: '20px 10px',
                  borderRadius: '8px',
                }}
              >
                {/* Name */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Name :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData.student?.fname} {viewData.student?.mname}{' '}
                      {viewData.student?.lname}
                    </div>
                  </Col>
                </Row>

                {/* Date */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Date :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {dayjs(viewData.date).format('DD-MM-YYYY')}
                    </div>
                  </Col>
                </Row>

                {/* Education */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Education :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData.educational_details}
                    </div>
                  </Col>
                </Row>

                {/* Incident */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Incident :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">{viewData.incident_analysis}</div>
                  </Col>
                </Row>

                {/* Observation */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Observation :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">{viewData.observations}</div>
                  </Col>
                </Row>

                {/* Remarks */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Remarks :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">{viewData.remarks}</div>
                  </Col>
                </Row>

                {/* Reported By */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Reported By :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">{viewData.report_by}</div>
                  </Col>
                </Row>
              </div>
            )}
          </Modal>

          {/* ===== DELETE MODAL ===== */}
          <Modal
            title="Confirm Delete"
            open={deleteModal}
            onOk={handleDelete}
            onCancel={() => setDeleteModal(false)}
          >
            Are you sure you want to delete this record?
          </Modal>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default CounsellorForm
